<script setup>
import {shallowRef, onBeforeMount, onBeforeUnmount, watch} from 'vue';
import {useQueryRepository} from '@/core/repository/query';
import {useQueryBus} from '@/core/plugins/queryBus';
import {updateUrlParams, getParamValue} from '@/core/helpers/urlParams';
import {CHART_TYPE} from '@/core/constants/query';

const props = defineProps({
    chartType: {
        type: String,
        default: () => CHART_TYPE.CFD,
    },
});

const emit = defineEmits(['change']);

const queryId = shallowRef(null);
const queryList = shallowRef(null);
const selectedQuery = shallowRef(null);

function updateSelectedQuery() {
    queryId.value = getParamValue('query_id');

    if (queryId.value && queryList.value?.length) {
        selectedQuery.value = queryList.value.find(({id}) => id === +queryId.value);
    } else {
        selectedQuery.value = {};
    }
}

async function handleGetQueryList() {
    queryList.value = await useQueryRepository().getQueryList(props.chartType);

    updateSelectedQuery();
}

async function handleGetQuery(query_id = null) {
    if (!query_id) return;

    selectedQuery.value = await useQueryRepository().getQuery(query_id);
    updateUrlParams({query_id: selectedQuery.value.id});

    if (typeof window.addFilter === 'function') {
        document.querySelector('#filters-table').innerHTML = '';
        Object.entries(selectedQuery.value.filters).forEach(([field, {operator, values}]) => {
            window.addFilter(field, operator, values);
        });
    }
}

onBeforeMount(() => {
    queryId.value = getParamValue('query_id');
    handleGetQueryList();
    useQueryBus().onListUpdate(handleGetQueryList);

    window.addEventListener('updatestate', updateSelectedQuery);
});

onBeforeUnmount(() => {
    window.removeEventListener('updatestate', updateSelectedQuery);
});

watch(selectedQuery, (newQuery) => {
    emit('change', newQuery);
}, {deep: true});
</script>

<template>
    <div
        v-if="queryList?.length"
        class="saved-chart-filters"
    >
        <select 
            id="chart-query-list"
            name="chart-query-list"
            class="saved-chart-filters__select"
            :value="selectedQuery?.id ?? ''"
            @change="({target}) => handleGetQuery(target.value)"
        >
            <option
                disabled
                selected
                value
            >
                {{ $t('filters.label_select_filter') }}
            </option>
            <option
                v-for="({id, name}) in queryList"
                :key="id"
                :value="id"
            >
                {{ name }}
            </option>
        </select>
    </div>
</template>

<style lang="scss">
.saved-chart-filters {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    width: max-content;

    &__label {
        font-size: 12px;
    }

    &__select {
        min-width: 150px;
    }
}
</style>
